'use client';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CENTERED_ICON, COLORS } from '../../constants';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

/**
 * Centered empty or error state: tinted icon circle, title, short body and an optional action, as in customer-mobile.
 *
 * @param {MaterialIconType} icon - Icon inside the circle.
 * @param {string} title - Main line.
 * @param {string} body - Optional explanation.
 * @param {string} actionLabel - Label of the optional button.
 * @param {Function} onAction - Click handler of the optional button.
 */
export const EmptyState = ({
  icon,
  title,
  body,
  actionLabel,
  onAction,
  headingLevel = 'h2',
}: {
  icon: MaterialIconType;
  title: string;
  body?: string;
  actionLabel?: string;
  onAction?: () => void;
  headingLevel?: 'h1' | 'h2' | 'h3';
}) => (
  <Stack
    data-testid="empty-state"
    sx={{ alignItems: 'center', textAlign: 'center', gap: '12px', px: 3, py: 6, width: '100%' }}
  >
    <Box
      aria-hidden="true"
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.surface.brandTint,
        color: COLORS.text.brand,
        mb: 1,
      }}
    >
      <MaterialIcon icon={icon} size={36} sx={CENTERED_ICON} />
    </Box>
    <Typography variant="h2" component={headingLevel} sx={{ color: COLORS.text.main }}>
      {title}
    </Typography>
    {body && (
      <Typography variant="body1" sx={{ color: COLORS.text.light, maxWidth: 280 }}>
        {body}
      </Typography>
    )}
    {actionLabel && onAction && (
      <Button onClick={onAction} sx={{ mt: 1, px: 4 }}>
        {actionLabel}
      </Button>
    )}
  </Stack>
);

/**
 * Error variant of EmptyState with a "Reintentar" action.
 *
 * @param {string} title - What failed, e.g. "No pudimos cargar tus operaciones".
 * @param {Function} onRetry - Retry handler.
 */
export const ErrorState = ({
  title = 'No pudimos cargar la información',
  body = 'Tuvimos un problema de nuestro lado. Intentá de nuevo en unos minutos.',
  onRetry,
  headingLevel,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
  headingLevel?: 'h1' | 'h2' | 'h3';
}) => (
  <EmptyState
    icon="sync_problem"
    title={title}
    body={body}
    actionLabel={onRetry ? 'Reintentar' : undefined}
    onAction={onRetry}
    headingLevel={headingLevel}
  />
);
